import { getEnvSettings } from '../configurationServices'
import { getFingerprint } from 'src/utils/fingerprint'
import {
  EnumReportOnlineCheckListRequest,
  EnumReportOnlineCheckListResponse,
  EnumReportOnlineCheckListError,
} from './types'

const { REACT_APP_PB_API_TICKET_INQUIRY_BASE_URL, OIDC_STORAGE_KEY } = getEnvSettings()

interface OidcUser {
  access_token: string
  token_type: string
  expires_at: number
}

/**
 * Gets the stored token from session storage using OIDC key
 */
const getStoredToken = (): string | null => {
  try {
    const storedData = sessionStorage.getItem(OIDC_STORAGE_KEY)

    if (!storedData) {
      console.error('getStoredToken - No OIDC data found in session storage')
      return null
    }

    const oidcUser: OidcUser = JSON.parse(storedData)
    return oidcUser?.access_token || null
  } catch (error) {
    console.error('getStoredToken - Error parsing OIDC data:', error)
    return null
  }
}

/**
 * Parameters for fetching enum report online check list
 */
export interface FetchEnumReportOnlineCheckListParams {
  adminUserId: string
  enumName: string         // Report type name (e.g., "Agencias")
  initialDate: string      // Start date in ISO format
  finalDate: string        // End date in ISO format
  entityId: number         // Entity ID (product or agency)
}

/**
 * Fetches the enum report online check list data
 * @param params - The parameters for the search request
 * @returns Promise with the list of financial flow entries
 */
export const fetchEnumReportOnlineCheckList = async (
  params: FetchEnumReportOnlineCheckListParams,
): Promise<EnumReportOnlineCheckListResponse> => {
  const token = getStoredToken()

  if (!token) {
    throw new EnumReportOnlineCheckListError(
      'No se encontró token de autenticación en el almacenamiento de sesión',
      'AUTH_ERROR',
    )
  }

  const fingerprint = await getFingerprint()

  const requestBody: EnumReportOnlineCheckListRequest = {
    enum_name: params.enumName,
    initial_date: params.initialDate,
    final_date: params.finalDate,
    entity_id: params.entityId,
    fingerprint: 'admin',
  }

  const response = await fetch(
    `${REACT_APP_PB_API_TICKET_INQUIRY_BASE_URL}/search/enumReportOnlineCheckList/89456-FG8IU`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'x-admin-user': params.adminUserId,
        'X-fingerprint': fingerprint,
        Origin: 'https://admin.placebet.com.do',
      },
      body: JSON.stringify(requestBody),
    },
  )

  let responseData: EnumReportOnlineCheckListResponse

  try {
    responseData = await response.json()
  } catch {
    throw new EnumReportOnlineCheckListError(
      `No se pudo procesar la respuesta del servidor: ${response.status} ${response.statusText}`,
      response.ok ? 'UNKNOWN' : `${response.status}`,
    )
  }

  // Check if response is ok
  if (!response.ok) {
    throw new EnumReportOnlineCheckListError(
      `Error al obtener los datos del reporte (${response.status})`,
      `${response.status}`,
    )
  }

  // Validate response data structure
  if (!responseData.data || !Array.isArray(responseData.data)) {
    throw new EnumReportOnlineCheckListError(
      'Respuesta del servidor inválida',
      'INVALID_RESPONSE',
    )
  }

  return responseData
}
